"use client";

import { useEffect, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { useWalletConnectModalContext } from "../providers/wallet-connect-modal-provider";
import { resolveSocial, resolveWallet } from "../constants";
import { CloseIcon } from "./icons";
import { cn } from "../../../lib/utils";
import {
  TrackingBoundary,
  normalizeFailureReason,
  trackDappUiFunnel,
} from "../../../lib/sentry/index";
import type {
  SocialConfig,
  WalletConnectModalContentProps,
  WalletConfig,
} from "../types";

function runConnectHandler(
  id: string,
  handler: (() => void | Promise<void>) | undefined,
) {
  trackDappUiFunnel("connect", "wallet_selected", { tags: { item_id: id } });
  const result = handler?.();
  if (result instanceof Promise) {
    result.then(
      () => trackDappUiFunnel("connect", "connected"),
      (error) =>
        trackDappUiFunnel("connect", "connected", {
          status: "failure",
          reason: normalizeFailureReason(error),
        }),
    );
  }
}

function WalletItem({
  wallet,
  detected,
  onConnect,
}: {
  wallet: WalletConfig;
  detected: boolean;
  onConnect: () => void;
}) {
  const handleClick = () => {
    if (wallet.installUrl && !detected) {
      window.open(wallet.installUrl, "_blank", "noopener,noreferrer");
      return;
    }
    onConnect();
  };

  const Icon = wallet.icon;
  const featured = wallet.featured === true;

  return (
    <button
      type="button"
      className={cn("wcm-wallet-item", featured && "wcm-wallet-item--featured")}
      data-track="wallet_item_click"
      data-track-id={wallet.id}
      onClick={handleClick}
    >
      <div className="wcm-wallet-item-left">
        <div className="wcm-wallet-icon">
          <Icon />
        </div>
        <div className="wcm-wallet-name-row">
          <span className="wcm-wallet-name">{wallet.name}</span>
          {wallet.badge && (
            <span className="wcm-wallet-badge">{wallet.badge}</span>
          )}
        </div>
      </div>
      {wallet.installUrl &&
        (detected ? (
          <span className="wcm-installed-label">Installed</span>
        ) : (
          <span className="wcm-install-btn">Install</span>
        ))}
    </button>
  );
}

function SocialItem({
  social,
  onSignIn,
}: {
  social: SocialConfig;
  onSignIn: () => void;
}) {
  const Icon = social.icon;
  const label = `Sign in with ${social.name}`;
  return (
    <button
      type="button"
      className="wcm-social-item"
      data-provider={social.id}
      data-track="social_provider_click"
      data-track-id={social.id}
      onClick={onSignIn}
      aria-label={label}
    >
      <span className="wcm-social-icon">
        <Icon />
      </span>
      <span className="wcm-social-name">{label}</span>
    </button>
  );
}

function ModalBody() {
  const {
    wallets,
    activeWalletIds,
    socialProviders,
    activeSocialIds,
    termsUrl,
    privacyUrl,
    isDetected,
    close,
    isMobile,
  } = useWalletConnectModalContext();

  const visibleIds = activeWalletIds.filter((id) => {
    const v = resolveWallet(id).visibility;
    if (!v || v === "always") return true;
    if (v === "desktop-only") return !isMobile;
    if (v === "mobile-only") return isMobile;
    return true;
  });

  const hasSocial = activeSocialIds.length > 0;
  const hasWallets = visibleIds.length > 0;

  return (
    <>
      <div className="wcm-header">
        <div className="wcm-header-text">
          <h2 className="wcm-title">CROSS Wallet</h2>
          {!isMobile && (
            <p className="wcm-subtitle">
              Sign in instantly with your social account
            </p>
          )}
        </div>
        <button
          type="button"
          className="wcm-close-btn"
          data-track="close"
          onClick={close}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="wcm-body">
        {hasSocial && (
          <>
            {!isMobile && (
              <div className="wcm-section-header">
                <span className="wcm-section-title">
                  Social Account Wallet Connect
                </span>
                <span className="wcm-recommended-badge">Recommended</span>
              </div>
            )}
            <div className="wcm-social-grid">
              {activeSocialIds.map((id) => {
                const social = resolveSocial(id);
                return (
                  <SocialItem
                    key={id}
                    social={social}
                    onSignIn={() => {
                      runConnectHandler(id, socialProviders[id]);
                      close();
                    }}
                  />
                );
              })}
            </div>
            {hasWallets && (
              <div className="wcm-separator">
                <div className="wcm-separator-line" />
                <span className="wcm-separator-text">Other Wallets</span>
                <div className="wcm-separator-line" />
              </div>
            )}
          </>
        )}

        {hasWallets && (
          <div className="wcm-wallet-list">
            {visibleIds.map((id) => {
              const wallet = resolveWallet(id);
              const detected = wallet.rdns ? isDetected(wallet.rdns) : false;
              return (
                <WalletItem
                  key={id}
                  wallet={wallet}
                  detected={detected}
                  onConnect={() => {
                    runConnectHandler(id, wallets[id]);
                    close();
                  }}
                />
              );
            })}
          </div>
        )}

        <p className="wcm-terms">
          By continuing, you agree to NEXUS{" "}
          <TermsLink href={termsUrl} track="terms_link">
            Terms of Service
          </TermsLink>{" "}
          and consent to its{" "}
          <TermsLink href={privacyUrl} track="privacy_link">
            Privacy Policy
          </TermsLink>
          .
        </p>
      </div>
    </>
  );
}

function TermsLink({
  href,
  track,
  children,
}: {
  href?: string;
  track: string;
  children: ReactNode;
}) {
  if (!href) return <span className="wcm-terms-link">{children}</span>;
  return (
    <a
      className="wcm-terms-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-track={track}
    >
      {children}
    </a>
  );
}

export function WalletConnectModalContent({
  className,
}: WalletConnectModalContentProps) {
  const { isMobile, theme, portalStyle, open } = useWalletConnectModalContext();

  useEffect(() => {
    if (open) trackDappUiFunnel("connect", "opened");
  }, [open]);

  if (isMobile) {
    return (
      <TrackingBoundary feature="wallet_connect">
        <Drawer.Portal>
          <Drawer.Overlay className="wcm-overlay" />
          <Drawer.Content
            className={cn("wcm-drawer-content", className)}
            data-theme={theme}
            style={portalStyle}
          >
            <Drawer.Title className="wcm-sr-only">Connect Wallet</Drawer.Title>
            <div className="wcm-drawer-handle" />
            <div className="wcm-drawer-body">
              <ModalBody />
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </TrackingBoundary>
    );
  }

  return (
    <TrackingBoundary feature="wallet_connect">
      <Dialog.Portal>
        <Dialog.Overlay className="wcm-overlay" />
        <Dialog.Content
          className={cn("wcm-dialog-content", className)}
          data-theme={theme}
          style={portalStyle}
        >
          <Dialog.Title className="wcm-sr-only">Connect Wallet</Dialog.Title>
          <Dialog.Description className="wcm-sr-only">
            Sign in with a social provider or pick a wallet to connect.
          </Dialog.Description>
          <div className="wcm-dialog-body">
            <ModalBody />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </TrackingBoundary>
  );
}
